import { createSlice } from "@reduxjs/toolkit";

const USD_CURRENCY = {
  id: 1,
  code: "USD",
  name: "US Dollar(USD)",
  rate: 1,
  created: 1710330643000,
  country: "US",
};
const NGN_CURRENCY = {
  id: 2,
  code: "NGN",
  name: "Nigerian Naira (NGN)",
  rate: 1,
  created: 1710330643000,
  country: "NG",
};
const CAD_CURRENCY = {
  id: 3,
  code: "CAD",
  name: "Canadian Dollar (CAD)",
  rate: 1,
  created: 1710330643000,
  country: "CA",
};

// Currencies searches can be priced in. Also the picker's list when the
// backend has no /country catalog to supply one.
export const SUPPORTED_CURRENCIES = [USD_CURRENCY, NGN_CURRENCY, CAD_CURRENCY];

// Visitor country -> pricing currency. Anywhere not listed prices in USD.
const COUNTRY_CURRENCY: Record<string, any> = {
  NG: NGN_CURRENCY,
  CA: CAD_CURRENCY,
};

// Set only when the user picks a currency themselves, so an auto-detected
// one can still be re-derived (e.g. Canadians previously defaulted to USD).
const MANUAL_CURRENCY_KEY = "currencyManual";

interface AppState {
  loading: boolean;
  currency: any;
  city: any;
  airport: any;
  providers: any;
  countries: any;
  info: any;
  airport_route: any;
  airlineSearch: any;
  airportInfo: any;
  pastSearches: any;
  frequentTraveler: any;
  dateOpen: boolean;
}

const initialState: AppState = {
  loading: false,
  city: null,
  providers: null,
  countries: null,
  currency: localStorage.getItem("currency")
    ? /* @ts-ignore */
      JSON.parse(localStorage.getItem("currency"))
    : USD_CURRENCY,
  airport: null,
  info: null,
  airportInfo: null,
  airport_route: null,
  airlineSearch: null,
  pastSearches: null,
  frequentTraveler: [],
  dateOpen: false,
};

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    loading: (state) => {
      state.loading = true;
    },
    currencySelect: (state, action) => {
      state.currency = action.payload;
      localStorage.setItem("currency", JSON.stringify(action.payload));
      localStorage.setItem(MANUAL_CURRENCY_KEY, "1");
    },
    cityLoaded: (state, action) => {
      state.loading = false;
      state.city = arrayToHashMap(action.payload);
    },
    countryLoaded: (state: any, action: any) => {
      state.loading = false;
      state.countries = action.payload;
      resolveAutoCurrency(state);
    },
    airportLoaded: (state, action) => {
      state.loading = false;
      state.airport = action.payload;
    },
    providersLoaded: (state, action) => {
      state.loading = false;
      state.providers = formatProvider(action.payload);
    },
    failed: (state) => {
      state.loading = false;
    },
    airportRouteLoaded: (state, action) => {
      state.loading = false;
      state.airport_route = action.payload;
    },
    airlineSearchLoaded: (state, action) => {
      state.loading = false;
      state.airlineSearch = action.payload;
    },
    clientAirportLoaded: (state, action) => {
      state.loading = false;
      state.airportInfo = action.payload.length > 0 && action.payload[0];
    },
    pastSearchLoaded: (state, action) => {
      state.loading = false;
      state.pastSearches = action.payload;
    },
    frequentTravelerLoaded: (state, action) => {
      state.loading = false;
      state.frequentTraveler = action.payload;
    },
    clientCountryInfoLoaded: (state, action) => {
      state.loading = false;
      console.log(action.payload);
      //   state.currency = action.payload;
      localStorage.setItem("currency", JSON.stringify(action.payload));
    },
    clientInfoLoaded: (state, action) => {
      state.loading = false;
      state.info = action.payload;
      resolveAutoCurrency(state);
    },
    setDateOpen: (state, action) => {
      state.dateOpen = action.payload;
    },
  },
});

// Nigeria prices in NGN, Canada in CAD, everywhere else in USD. Resolves
// straight off GET /client/info ({ model: { ip, countryCode } }) — the
// backend has no /country catalog endpoint, so `state.countries` can't be
// relied on to ever populate. If a countries list does show up later (e.g.
// a different backend), prefer its entry for a more authoritative object;
// otherwise fall back to the static shapes above.
function resolveAutoCurrency(state: any) {
  // Never override a currency the user picked themselves.
  if (localStorage.getItem(MANUAL_CURRENCY_KEY)) return;
  if (!state.info) return;

  const countryCode = state.info.model?.countryCode;
  const fallback = COUNTRY_CURRENCY[countryCode] || USD_CURRENCY;

  const match = (state.countries || []).find(
    (x: any) => x.country === fallback.country,
  );
  const resolved = match || fallback;

  state.currency = resolved;
  localStorage.setItem("currency", JSON.stringify(resolved));
}
function arrayToHashMap(array: any) {
  const hashMap: any = {};

  array.forEach((item: any) => {
    const code = item.code;
    hashMap[code] = item;
  });

  return hashMap;
}
const formatProvider = (data: any) => {
  let sorted = {};
  for (const item of data) {
    sorted = { ...sorted, [item.ref]: item };
  }
  return sorted;
};

export const {
  loading,
  currencySelect,
  cityLoaded,
  failed,
  airportLoaded,
  providersLoaded,
  countryLoaded,
  clientInfoLoaded,
  airlineSearchLoaded,
  airportRouteLoaded,
  clientAirportLoaded,
  pastSearchLoaded,
  clientCountryInfoLoaded,
  frequentTravelerLoaded,
  setDateOpen,
} = appSlice.actions;

export default appSlice.reducer;
